import { observable } from 'mobx';
import { isEmpty } from 'lodash';

export type HotelOrderItemEanTodoMapperResponseArgs = {
  reservation_number: string;
  people_num: number;
  hotel_name: string;
  hotel_address: string;
  room_type: string;
  plan_name: string;
  breakfast: boolean;
  checkin_date: string;
  checkout_date: string;
  traveler_name: string;
};

export type HotelOrderItemEanTodoResponseArgs = {
  from_order_item: HotelOrderItemEanTodoMapperResponseArgs;
  from_ean_record: HotelOrderItemEanTodoMapperResponseArgs;
  diff: { [key: string]: string };
  price: number;
  original_price: number;
  hotel_price: number;
  supplied_item_name: string;
  is_inventoried: boolean;
  inventoried_by: string;
  inventoried_at: string;
  trip_id: number;
  order_item_id: number;
  trace_id: number;
  restricted: boolean;
  user_checker: boolean;
  last_log_message: string;
  last_log_message_arranger: string;
  original_plan_name: string;
  show_multiple_hotel_plans: boolean;
  special_checkin_instructions: string | null;
  ean_phase: string | null;
  ean_api_error_message: string | null;
};

type HotelOrderItemEanTodoMapperArgs = {
  reservationNumber: string;
  peopleNum: number;
  hotelName: string;
  hotelAddress: string;
  roomType: string;
  planName: string;
  breakfast: boolean;
  checkinDate: string;
  checkoutDate: string;
  travelerName: string;
};

type HotelOrderItemEanTodoArgs = {
  fromOrderItem: HotelOrderItemEanTodoMapperArgs;
  fromEanRecord: HotelOrderItemEanTodoMapperArgs;
  diff: { [key: string]: boolean };
  price: number;
  originalPrice: number;
  hotelPrice: number;
  suppliedItemName: string;
  isInventoried: boolean;
  inventoriedBy: string;
  inventoriedAt: string;
  tripId: number;
  orderItemId: number;
  traceId: number;
  restricted: boolean;
  userChecker: boolean;
  lastLogMessage: string;
  lastLogMessageArranger: string;
  originalPlanName: string;
  showMultipleHotelPlans: boolean;
  specialCheckinInstructions: string | null;
  eanPhase: string | null;
  eanApiErrorMessage: string | null;
};

const convertMapperResponseToArgs = (
  response: HotelOrderItemEanTodoMapperResponseArgs
): HotelOrderItemEanTodoMapperArgs => ({
  reservationNumber: response.reservation_number,
  peopleNum: response.people_num,
  hotelName: response.hotel_name,
  hotelAddress: response.hotel_address,
  roomType: response.room_type,
  planName: response.plan_name,
  breakfast: response.breakfast,
  checkinDate: response.checkin_date,
  checkoutDate: response.checkout_date,
  travelerName: response.traveler_name
});

const convertResponseToArgs = (response: HotelOrderItemEanTodoResponseArgs): HotelOrderItemEanTodoArgs => ({
  fromOrderItem: convertMapperResponseToArgs(response.from_order_item),
  fromEanRecord: convertMapperResponseToArgs(response.from_ean_record),
  diff: Object.entries(response.diff).reduce(
    (acc, [key, value]) => ({ ...acc, [key]: !isEmpty(value) }),
    {} as { [key: string]: boolean }
  ),
  price: response.price,
  originalPrice: response.original_price,
  hotelPrice: response.hotel_price,
  suppliedItemName: response.supplied_item_name,
  isInventoried: response.is_inventoried,
  inventoriedBy: response.inventoried_by,
  inventoriedAt: response.inventoried_at,
  tripId: response.trip_id,
  orderItemId: response.order_item_id,
  traceId: response.trace_id,
  restricted: response.restricted,
  userChecker: response.user_checker,
  lastLogMessage: response.last_log_message,
  lastLogMessageArranger: response.last_log_message_arranger,
  originalPlanName: response.original_plan_name,
  showMultipleHotelPlans: response.show_multiple_hotel_plans,
  specialCheckinInstructions: response.special_checkin_instructions,
  eanPhase: response.ean_phase,
  eanApiErrorMessage: response.ean_api_error_message
});

export default class HotelOrderItemEanTodo {
  @observable readonly fromOrderItem: HotelOrderItemEanTodoMapperArgs;

  @observable readonly fromEanRecord: HotelOrderItemEanTodoMapperArgs;

  @observable readonly diff: { [key: string]: boolean };

  @observable readonly price: number;

  @observable readonly originalPrice: number;

  @observable readonly hotelPrice: number;

  @observable readonly suppliedItemName: string;

  @observable readonly isInventoried: boolean;

  @observable readonly inventoriedBy: string;

  @observable readonly inventoriedAt: string;

  @observable readonly tripId: number;

  @observable readonly orderItemId: number;

  @observable readonly traceId: number;

  @observable readonly restricted: boolean;

  @observable readonly userChecker: boolean;

  @observable readonly lastLogMessage: string;

  @observable readonly lastLogMessageArranger: string;

  @observable readonly isDiff: boolean;

  @observable readonly originalPlanName: string;

  @observable readonly showMultipleHotelPlans: boolean;

  @observable readonly specialCheckinInstructions: string | null;

  @observable readonly eanPhase: string | null;

  @observable readonly eanApiErrorMessage: string | null;

  constructor(args: HotelOrderItemEanTodoArgs) {
    this.fromOrderItem = args.fromOrderItem;
    this.fromEanRecord = args.fromEanRecord;
    this.diff = args.diff;
    this.price = args.price;
    this.originalPrice = args.originalPrice;
    this.hotelPrice = args.hotelPrice;
    this.suppliedItemName = args.suppliedItemName;
    this.isInventoried = args.isInventoried;
    this.inventoriedBy = args.inventoriedBy;
    this.inventoriedAt = args.inventoriedAt;
    this.tripId = args.tripId;
    this.orderItemId = args.orderItemId;
    this.traceId = args.traceId;
    this.restricted = args.restricted;
    this.userChecker = args.userChecker;
    this.lastLogMessage = args.lastLogMessage;
    this.lastLogMessageArranger = args.lastLogMessageArranger;
    this.isDiff = Object.values(this.diff).some(v => v);
    this.originalPlanName = args.originalPlanName;
    this.showMultipleHotelPlans = args.showMultipleHotelPlans;
    this.specialCheckinInstructions = args.specialCheckinInstructions;
    this.eanPhase = args.eanPhase;
    this.eanApiErrorMessage = args.eanApiErrorMessage;
  }

  isDiffPlanName(): boolean {
    return this.diff.plan_name;
  }

  userCheckerStr(): string | null {
    if (this.fromOrderItem.planName !== this.originalPlanName) {
      return null;
    }

    if (!this.restricted) {
      return null;
    }

    return this.userChecker ? 'チェック済み' : '未チェック';
  }

  static fromResponse(response: HotelOrderItemEanTodoResponseArgs) {
    const args = convertResponseToArgs(response);
    return new HotelOrderItemEanTodo(args);
  }
}
